import { Box, Grid2, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  checkCanUpdate,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import CustomSwitch from "../../../components/CustomSwitch/CustomSwitch";
import {
  createDoseLevelService,
  fetchDoseLevelByIdService,
  updateDoseLevelService,
} from "../../../services/doseLevelManagementService";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";

const AddUpdateDoseLevel = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [canUpdateDoseLevel, setCanUpdateDoseLevel] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const role = localStorage.getItem("role");

  const createDoseSchema = yup.object().shape({
    name: yup
      .string()
      .required("Name is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Name must not start with a number or special character"
      )
      .test("has-letter", "Name must contain at least one alphabet", (value) =>
        /[A-Za-z]/.test(value || "")
      ),
    mgDose: yup
      .string()
      .matches(/^\d+(\.\d+)?$/, "Mg dose is not valid.")
      .required("Mg dose is required"),
    isEnabled: yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createDoseSchema),
    defaultValues: {
      name: "",
      mgDose: "",
      isEnabled: false,
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanEditDosageLevel"
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateDoseLevel(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateDoseLevel(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (id) {
      getDoseLevelById(id);
    }
  }, [id]);

  useEffect(() => {
    if (canUpdateDoseLevel) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateDoseLevel]);

  const getDoseLevelById = async (id) => {
    setLoad(true);
    try {
      const res = await fetchDoseLevelByIdService(access_token, id);
      if (res?.status == 200) {
        const resp = res?.data?.result;
        setLoad(false);
        reset({
          mgDose: resp?.mgDose,
          name: resp?.name,
          isEnabled: resp?.isEnabled,
        });
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const addUpdateDoseLevel = async (data) => {
    setLoad(true);
    const newData = {
      name: data?.name,
      isRecommended: false,
      isEnabled: data?.isEnabled,
      mgDose: data?.mgDose,
    };

    try {
      if (!id) {
        const res = await createDoseLevelService(access_token, newData);
        if (res?.data?.status == 409) {
          setLoad(false);
          notifyError("Dose Level already exists !");
          return;
        } else if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Dose Level is created successfully !");
          reset();
          navigate("/dose-level-management");
        }
      } else {
        const res = await updateDoseLevelService(access_token, newData, id);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Dose Level is Updated successfully !");
          reset();
          navigate("/dose-level-management");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const { onChange, ...rest } = register("mgDose");
  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(
                  e,
                  isDirty,
                  "dose-level-management",
                  navigate
                )
              }
            >
              Dose Level Management
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              to="/addUpdate-dose-level-management"
            >
              {id ? "Edit Dose Level Management" : "Add Dose Level"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Dose Level Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addUpdateDoseLevel)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Name
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    type={"text"}
                    disabled={isReadOnly}
                    {...register("name")}
                  />
                </div>
                {errors.name && (
                  <div className="error-container">{errors.name.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Mg Dose
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={isReadOnly}
                    type={"text"}
                    {...rest}
                    onChange={(e) => {
                      // let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
                      // if (value !== "0") value = value.replace(/^0+/, ""); // Remove leading zeros
                      // e.target.value = value;

                      // onChange(e); // Important: Notify react-hook-form
                      let value = e.target.value;

                      // Remove all characters except digits and dot
                      value = value.replace(/[^0-9.]/g, "");

                      // Allow only one decimal point
                      const parts = value.split(".");
                      if (parts.length > 2) {
                        value = parts[0] + "." + parts.slice(1).join(""); // Merge multiple dots
                      }

                      // Restrict to 2 decimal places
                      if (parts[1]?.length > 2) {
                        value = parts[0] + "." + parts[1].slice(0, 2);
                      }

                      // Remove leading zeros unless it's like "0."
                      if (
                        value.startsWith("0") &&
                        !value.startsWith("0.") &&
                        value.length > 1
                      ) {
                        value = value.replace(/^0+/, "");
                      }

                      e.target.value = value;
                      onChange(e);
                    }}
                  />
                </div>
                {errors.mgDose && (
                  <div className="error-container">{errors.mgDose.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Is Enabled</p>
                  <Controller
                    name="isEnabled"
                    control={control}
                    disabled={isReadOnly}
                    defaultValue={false} // or your actual initial value
                    render={({ field }) => (
                      <CustomSwitch
                        isSwitch={field.value}
                        disabled={isReadOnly}
                        setSwitch={(val) => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "dose-level-management",
                      navigate
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                {checkCanUpdate(id, isReadOnly, role) && (
                  <button
                    type="submit"
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled-btn" : "create-btn"}
                  >
                    {id ? "Update" : "Save"}
                  </button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddUpdateDoseLevel;
